/**
 * @fileoverview Review step content for import-template authoring.
 * @module import-editor/review-tab-content
 *
 * Responsibilities:
 * - Summarize template configuration before final persistence
 * - Surface validation status across authoring tabs
 * - Offer explicit draft-save versus publish actions
 *
 * Dependencies:
 * - `../templateEditor/types` for state shape
 * - `../utils/templateValidation` for tab-level validation rules
 *
 * Architecture:
 * - Presentation and validation summary only
 * - No direct persistence logic; actions are delegated upward
 *
 * Notes:
 * - The primary action publishes the template or publishes-and-imports for Gate 3
 */

import { CheckCircle, AlertCircle, ChevronRight } from 'lucide-react';
import type { TemplateEditorState, TemplateEditorTab } from '../templateEditor/types';
import { validateTab } from '../utils/templateValidation';

// The review step lists only built-in tabs for quick jumps back to fix
// errors, but it is now reachable from host-injected tab lists too. The
// prop type therefore uses the generic descriptor and the component
// filters to built-in ids at runtime when reporting validation.
type TabDef = { id: string; label: string };

export interface ReviewTabContentProps {
  state: TemplateEditorState;
  tabs: TabDef[];
  onGoToTab: (tab: string) => void;
  onSaveDraft: () => void;
  onSaveTemplate: () => void;
  saving?: boolean;
  saveError?: string | null;
  /** Overrides the publish button label — used by the Card 3
      save-and-import flow to surface the parked tenant in the CTA. */
  saveTemplateLabel?: string;
}

export default function ReviewTabContent({
  state,
  tabs,
  onGoToTab,
  onSaveDraft,
  onSaveTemplate,
  saving = false,
  saveError,
  saveTemplateLabel,
}: ReviewTabContentProps) {
  // Shell-mode templates skip the mappings tab (it's not shown and not
  // validated); standalone/bound templates validate all four config tabs.
  const isShell = state.mode === 'shell';
  const reviewTabs: TemplateEditorTab[] = isShell
    ? ['basics', 'matchKey', 'geocoding']
    : ['basics', 'mappings', 'matchKey', 'geocoding'];

  const tabResults = reviewTabs.map(id => ({
    id,
    label: tabs.find(t => t.id === id)?.label ?? id,
    result: validateTab(id, state),
  }));

  const isFullyValid = tabResults.every(t => t.result.valid);

  return (
    <div className="space-y-6 max-w-xl">
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <div className="bg-gray-50 border-b border-gray-200 px-4 py-2">
          <h3 className="text-xs font-semibold text-gray-700">Validation Status</h3>
        </div>
        <div className="divide-y divide-gray-100">
          {tabResults.map(({ id, label, result }) => (
            <div key={id} className="flex items-center justify-between px-4 py-3">
              <div className="flex items-center gap-2">
                {result.valid
                  ? <CheckCircle className="h-4 w-4 text-green-500 shrink-0" />
                  : <AlertCircle className="h-4 w-4 text-red-500 shrink-0" />
                }
                <div>
                  <div className={`text-xs font-medium ${result.valid ? 'text-gray-700' : 'text-red-700'}`}>
                    {label}
                  </div>
                  {!result.valid && (
                    <div className="text-[11px] text-red-600 mt-0.5 space-y-0.5">
                      {result.errors.map(e => <div key={e}>• {e}</div>)}
                    </div>
                  )}
                </div>
              </div>
              {!result.valid && (
                <button
                  type="button"
                  onClick={() => onGoToTab(id)}
                  className="inline-flex items-center gap-0.5 text-[11px] text-blue-600 hover:text-blue-800 shrink-0 ml-2"
                >
                  Fix <ChevronRight className="h-3 w-3" />
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <div className="bg-gray-50 border-b border-gray-200 px-4 py-2">
          <h3 className="text-xs font-semibold text-gray-700">Template Summary</h3>
        </div>
        <div className="px-4 py-3 space-y-3 text-xs">
          <Row label="Name"        value={state.name || '—'} />
          <Row label="Data Mode"   value={state.dataMode} />
          <Row label="Behavior"    value={state.importBehavior} />
          <Row
            label="Field Mappings"
            value={isShell
              ? 'Shell template — mapping is performed at import time.'
              : `${state.fieldMappings.length} mapping${state.fieldMappings.length !== 1 ? 's' : ''}`
            }
          />
          <Row
            label="Match Keys"
            value={isShell
              ? (state.matchKeyField || 'None')
              : (state.fieldMappings.filter(m => m.isMatchKey).map(m => m.targetField).join(', ') || 'None')
            }
          />
          <Row
            label="Geocoding"
            value={state.geocodingEnabled
              ? `Enabled (${state.geocodingAddressFields.length} address field${state.geocodingAddressFields.length !== 1 ? 's' : ''})`
              : 'Disabled'
            }
          />
          {state.dataMode !== 'lookup' && (
            <Row
              label="Boundary Check"
              value={state.boundaryCheckEnabled ? 'Enabled — records outside boundary skipped' : 'Disabled'}
            />
          )}
          {state.expectedFileTypes.length > 0 && (
            <Row label="File Types" value={state.expectedFileTypes.map(f => `.${f}`).join(', ')} />
          )}
        </div>
      </div>

      {saveError && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-xs text-red-700">
          {saveError}
        </div>
      )}
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-start gap-2">
      <span className="w-32 shrink-0 text-gray-500">{label}</span>
      <span className="text-gray-800 font-medium">{value}</span>
    </div>
  );
}
